/**
 * Persisted named drawing sets per (user, symbol). The actual on-screen
 * drawings live in `ui.fibRetracements / horizontalLines / verticalLines`;
 * this slice holds the *saved* sets that can be loaded back later via
 * the Drawings dialog.
 *
 * The server contract is a bulk replace — the slice keeps the latest
 * snapshot per symbol; save/delete actions mutate the snapshot
 * locally and dispatch a single PUT.
 */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type { DrawingsPayload, SavedDrawingSet } from "@/api/types";
import { clearAllDrawings } from "./uiSlice";

interface DrawingSetsState {
  /** Saved sets for the currently-loaded symbol. Cleared on symbol
   *  change; refetched via `fetchDrawings`. */
  sets: Record<string, SavedDrawingSet>;
  loadedSymbol: string | null;
  /** Name of the set the user is currently "in" — set when they Load
   *  one or Save (as) a new one. Save (without a name input) writes
   *  back to this. null = unsaved working copy, must use Save-as to
   *  persist. Cleared by `New` / `Clear all`. */
  activeName: string | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  saving: boolean;
  error: string | null;
}

const initialState: DrawingSetsState = {
  sets: {},
  loadedSymbol: null,
  activeName: null,
  status: "idle",
  saving: false,
  error: null,
};

export const fetchDrawings = createAsyncThunk<
  { symbol: string; payload: DrawingsPayload },
  string
>("drawingSets/fetch", async (symbol) => {
  const payload = await api.getDrawings(symbol);
  return { symbol, payload };
});

export const saveDrawings = createAsyncThunk<
  { symbol: string; payload: DrawingsPayload },
  { symbol: string; sets: Record<string, SavedDrawingSet> }
>("drawingSets/save", async ({ symbol, sets }) => {
  const payload = await api.saveDrawings(symbol, { sets });
  return { symbol, payload };
});

const drawingSetsSlice = createSlice({
  name: "drawingSets",
  initialState,
  reducers: {
    // Local-only — used by the dialog before the user clicks Save.
    setLocalDrawings(state, action: PayloadAction<Record<string, SavedDrawingSet>>) {
      state.sets = action.payload;
    },
    setActiveName(state, action: PayloadAction<string | null>) {
      state.activeName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrawings.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDrawings.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.sets = action.payload.payload.sets;
        state.loadedSymbol = action.payload.symbol;
      })
      .addCase(fetchDrawings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load drawings";
      })
      .addCase(saveDrawings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveDrawings.fulfilled, (state, action) => {
        state.saving = false;
        state.sets = action.payload.payload.sets;
        state.loadedSymbol = action.payload.symbol;
      })
      .addCase(saveDrawings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save drawings";
      })
      // Cross-slice: wiping the chart un-tracks the active set so the
      // user can Load it again immediately. The dialog disables Load
      // for the active set to avoid no-op reloads — without this
      // handler, the toolbar's trash icon left activeName dangling
      // and Load stayed disabled until they clicked "New" in the
      // dialog.
      .addCase(clearAllDrawings, (state) => {
        state.activeName = null;
      });
  },
});

export const { setLocalDrawings, setActiveName } = drawingSetsSlice.actions;
export default drawingSetsSlice.reducer;
